<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrencyByCode, getCurrencyForLocale } from '../utils/currencyUtils';
import { useConfig } from 'dashboard/composables/useConfig';
import FunnelAPI from '../../../../api/funnel';
import StageColorPicker from './StageColorPicker.vue';
import draggable from 'vuedraggable';
import agents from '../../../../api/agents';
import inboxesAPI from '../../../../api/inboxes';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import { reactive } from 'vue';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  funnelId: {
    type: [String, Number],
    default: null,
  },
  initialData: {
    type: Object,
    default: null,
  },
  externalSave: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved', 'close', 'metadata-updated']);
const { t, locale } = useI18n();
const { isStacklab } = useConfig();
const activeFormTab = ref('basic-data');
const loading = ref(false);
const errorMessage = ref('');

// Dados das metas
const goals = ref([]);
const newGoal = ref({
  id: '',
  type: 'conversion_rate',
  value: '',
  description: '',
  unit: 'percentage',
});

const editingGoal = ref(null);
const goalDialogRef = ref(null);

const goalModalTitle = computed(() =>
  editingGoal.value
    ? t('KANBAN.FUNNELS.FORM.GOALS.MODAL_EDIT_TITLE')
    : t('KANBAN.FUNNELS.FORM.GOALS.MODAL_NEW_TITLE')
);

const resetGoalForm = () => {
  editingGoal.value = null;
  newGoal.value = {
    id: '',
    type: 'conversion_rate',
    value: '',
    description: '',
    unit: 'percentage',
  };
};

const openNewGoalModal = () => {
  resetGoalForm();
  nextTick(() => goalDialogRef.value?.open());
};

const handleGoalDialogClose = () => {
  resetGoalForm();
};

// Motivos de perda e ganho
const lossReasons = ref([]);
const winReasons = ref([]);
const newReason = ref({
  type: 'loss', // 'loss' ou 'win'
  title: '',
  description: '',
});
const editingReason = ref(null);
const reasonDialogRef = ref(null);

const reasonModalTitle = computed(() =>
  editingReason.value
    ? t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.MODAL_EDIT_TITLE')
    : t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.MODAL_NEW_TITLE')
);

const resetReasonForm = () => {
  editingReason.value = null;
  newReason.value = { type: 'loss', title: '', description: '' };
};

const openNewReasonModal = () => {
  resetReasonForm();
  nextTick(() => reasonDialogRef.value?.open());
};

const handleReasonDialogClose = () => {
  resetReasonForm();
};

// Checklist presets
const selectedStageForChecklist = ref(null);
const newChecklistItem = ref({
  text: '',
  priority: 'none',
  required: false,
});
const editingChecklistItem = ref(null);
const checklistDialogRef = ref(null);

const checklistModalTitle = computed(() =>
  editingChecklistItem.value
    ? t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.MODAL_EDIT_TITLE')
    : t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.MODAL_NEW_TITLE')
);

const resetChecklistForm = () => {
  editingChecklistItem.value = null;
  newChecklistItem.value = { text: '', priority: 'none', required: false };
};

const openNewChecklistModal = stageId => {
  if (!stageId) return;
  selectedStageForChecklist.value = stageId;
  resetChecklistForm();
  nextTick(() => checklistDialogRef.value?.open());
};

const selectedStageNameForChecklistModal = computed(() => {
  const id = selectedStageForChecklist.value;
  if (!id) return '';
  return stages.value.find(s => s.id === id)?.name || '';
});

const handleChecklistDialogClose = () => {
  resetChecklistForm();
};

const formData = ref({
  name: '',
  description: '',
  active: true,
  stages: {},
  settings: {
    round_robin_distribution: false,
    require_loss_reason: false,
    default_currency: getCurrencyForLocale(locale.value).code,
  },
  agents: [],
  global_custom_attributes: [],
});

// Campos adicionais para exibição
const funnelMetadata = ref({
  updated_at: null,
  optimization_history: [],
});

const stages = ref([]);
const newStage = ref({
  name: '',
  color: '#FF6B6B',
  description: '',
  sla: null,
  capacity_limit: null,
  auto_create_conditions: [],
});

const editingStage = ref(null);
const stageDialogRef = ref(null);

const stageModalTitle = computed(() =>
  editingStage.value
    ? t('KANBAN.FUNNELS.FORM.STAGES.MODAL_EDIT_TITLE')
    : t('KANBAN.FUNNELS.FORM.STAGES.MODAL_NEW_TITLE')
);

const resetStageForm = () => {
  editingStage.value = null;
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    sla: null,
    capacity_limit: null,
    auto_create_conditions: [],
  };
};

const openNewStageModal = () => {
  resetStageForm();
  nextTick(() => stageDialogRef.value?.open());
};

const handleStageDialogClose = () => {
  resetStageForm();
};

const dragOptions = {
  animation: 150,
  ghostClass: 'ghost-card',
};

const vFocus = {
  mounted: el => el.focus(),
};

const agentsList = ref([]);
const loadingAgents = ref(false);
const existingStageIds = ref(new Set());

// Mantido para compatibilidade com bundles de dev em cache (HMR): o template atual
// não usa estes símbolos; após recarregar a página o aviso some.
const newCondition = ref({
  type: 'contact_has_tag',
  value: '',
  attribute: '',
  operator: 'equal_to',
});

// Array de abas do formulário
const formTabs = computed(() => [
  {
    id: 'basic-data',
    icon: 'file-text',
    label: t('KANBAN.FUNNELS.FORM.SECTIONS.BASIC_DATA'),
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text-icon lucide-file-text"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>`
  },
  {
    id: 'global-fields',
    icon: 'list-plus',
    label: t('KANBAN.FUNNELS.FORM.SECTIONS.GLOBAL_CUSTOM_FIELDS'),
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-plus-icon lucide-list-plus"><path d="M16 5H3"/><path d="M11 12H3"/><path d="M16 19H3"/><path d="M18 9v6"/><path d="M21 12h-6"/></svg>`
  },
  {
    id: 'settings',
    icon: 'settings',
    label: 'Configurações',
  },
  {
    id: 'goals',
    icon: 'crosshair',
    label: t('KANBAN.FUNNELS.FORM.SECTIONS.GOALS'),
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-crosshair-icon lucide-crosshair"><circle cx="12" cy="12" r="10"/><line x1="22" x2="18" y1="12" y2="12"/><line x1="6" x2="2" y1="12" y2="12"/><line x1="12" x2="12" y1="6" y2="2"/><line x1="12" x2="12" y1="22" y2="18"/></svg>`
  },
  {
    id: 'loss-win-reasons',
    icon: 'list-checks',
    label: t('KANBAN.FUNNELS.FORM.SECTIONS.LOSS_WIN_REASONS'),
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-checks-icon lucide-list-checks"><path d="M3 17h2"/><path d="M3 7h2"/><path d="M3 12h2"/><path d="M9 7l2 2 4-4"/><path d="M9 17l2 2 4-4"/><path d="M9 12h12"/></svg>`
  },
  {
    id: 'checklist-presets',
    icon: 'list-check',
    label: t('KANBAN.FUNNELS.FORM.SECTIONS.CHECKLIST_PRESETS'),
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-check-icon lucide-list-check"><path d="M16 5H3"/><path d="M16 12H3"/><path d="M11 19H3"/><path d="m15 18 2 2 4-4"/></svg>`
  },
]);


// Gera o ID baseado no nome
const generateId = name => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Gera um ID único apenas se necessário
const generateUniqueId = (baseName, existingStages, currentId = null) => {
  // Se já tem um ID e está editando, mantém o mesmo
  if (currentId && editingStage.value) {
    return currentId;
  }

  const baseId = generateId(baseName);
  const stageIds = existingStages.map(stage => stage.id);

  // Se o ID base não existe, usa ele
  if (!stageIds.includes(baseId)) {
    return baseId;
  }

  // Se existe, adiciona um timestamp para garantir unicidade
  return `${baseId}_${Date.now()}`;
};

// Atualiza o ID apenas para novas etapas
watch(
  () => newStage.value.name,
  newName => {
    if (newName && !editingStage.value) {
      newStage.value.id = generateId(newName);
    }
  }
);

// Converte as etapas para o formato esperado pela API
const formatStagesForAPI = () => {
  const formattedStages = {};

  // Garantir que as posições reflitam a ordem atual
  stages.value.forEach((stage, index) => {
    // Definir explicitamente a posição baseada no índice atual
    const position = index + 1;

    // Preserva metadados já existentes da etapa (ex: automações)
    const existingStageData = formData.value.stages?.[stage.id] || {};

    formattedStages[stage.id] = {
      ...existingStageData,
      name: stage.name,
      color: stage.color,
      position: position, // Garantir que a posição reflita a ordem atual
      description: stage.description,
      sla: stage.sla,
      capacity_limit: stage.capacity_limit,
      auto_create_conditions: stage.auto_create_conditions || [],
      message_templates: existingStageData.message_templates || [],
      checklist_templates: stage.checklist_templates || [],
    };
  });

  return formattedStages;
};

// Função para lidar com a reordenação das etapas
const handleOrderChange = async () => {
  if (!props.isEditing) return;

  try {
    // Atualizar as posições com base na nova ordem
    stages.value.forEach((stage, index) => {
      stage.position = index + 1;
    });

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      stages: formatStagesForAPI(),
    };

    // Atualiza o estado local sem fazer chamada à API
    formData.value.stages = payload.stages;
  } catch (error) {
    // Error handling
  }
};

const handleAddStage = e => {
  if (e?.target?.closest('.colorpicker--chrome')) {
    return;
  }

  if (!newStage.value.name) return;

  if (editingStage.value) {
    const index = stages.value.findIndex(s => s.id === editingStage.value.id);
    if (index !== -1) {
      stages.value[index] = {
        ...editingStage.value,
        name: newStage.value.name,
        color: newStage.value.color,
        description: newStage.value.description,
        sla: newStage.value.sla,
        capacity_limit: newStage.value.capacity_limit,
        auto_create_conditions: [...newStage.value.auto_create_conditions],
        checklist_templates: editingStage.value.checklist_templates || [],
        id: editingStage.value.id,
      };
      if (props.isEditing) {
        handleOrderChange();
      }
    }
    editingStage.value = null;
  } else {
    let stageId = generateId(newStage.value.name);
    let counter = 1;
    while (existingStageIds.value.has(stageId)) {
      stageId = `${generateId(newStage.value.name)}_${counter}`;
      counter++;
    }
    stages.value.push({
      ...newStage.value,
      id: stageId,
      checklist_templates: [],
    });
    existingStageIds.value.add(stageId);
  }
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    sla: null,
    capacity_limit: null,
    auto_create_conditions: [],
  };
  stageDialogRef.value?.close();
};

const removeStage = index => {
  stages.value.splice(index, 1);
};

// Busca todos os funis e coleta os IDs existentes
const fetchExistingStageIds = async () => {
  try {
    const { data: allFunnels } = await FunnelAPI.get();
    existingStageIds.value.clear();

    allFunnels.forEach(funnel => {
      if (funnel.id !== props.funnelId) {
        // Ignora o funil atual se estiver editando
        Object.keys(funnel.stages || {}).forEach(id => {
          existingStageIds.value.add(id);
        });
      }
    });
  } catch (error) {
    // Error handling
  }
};

// Inicializa o formulário
onMounted(async () => {
  // Carrega agentes e inboxes sempre
  await Promise.all([fetchAgents(), fetchInboxes()]);

  // Carrega dados do funil se estiver editando
  if (props.isEditing && props.funnelId) {
    await fetchFunnelData();
  }

  // Se estiver criando e existir initialData vindo de template, hidrata o formulário
  if (!props.isEditing && props.initialData) {
    formData.value.name = props.initialData.name || '';
    formData.value.description = props.initialData.description || '';
    formData.value.active = true;
    formData.value.stages = props.initialData.stages || {};
    formData.value.settings = props.initialData.settings || {};
    formData.value.agents = (props.initialData.settings?.agents || []).map(
      normalizeAgentForFunnel
    );

    // Inicializa lista de etapas ordenadas
    stages.value = Object.entries(formData.value.stages)
      .map(([id, stage]) => ({
        id,
        name: stage.name,
        color: stage.color,
        description: stage.description,
        sla: stage.sla,
        capacity_limit: stage.capacity_limit,
        position: parseInt(stage.position, 10) || 0,
        auto_create_conditions: [...(stage.auto_create_conditions || [])],
        message_templates: stage.message_templates || [],
        checklist_templates: [...(stage.checklist_templates || [])],
      }))
      .sort((a, b) => a.position - b.position);
  }

  // Garante que sempre tenha pelo menos um campo customizado vazio
  if (
    !Array.isArray(formData.value.global_custom_attributes) ||
    formData.value.global_custom_attributes.length === 0
  ) {
    formData.value.global_custom_attributes = [{ name: '', type: '', field_type: 'single', list_values: [] }];
  }

  // Watcher para salvamento externo
  watch(() => props.externalSave, (newValue) => {
    if (newValue) {
      handleSubmit();
    }
  });
});

const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    agentsList.value = data;
  } catch (error) {
    // Error handling
  } finally {
    loadingAgents.value = false;
  }
};

const fetchInboxes = async () => {
  try {
    loadingInboxes.value = true;
    const response = await inboxesAPI.get();
    inboxes.value = response.data.payload || [];
  } catch (error) {
    inboxes.value = [];
  } finally {
    loadingInboxes.value = false;
  }
};


const fetchFunnelData = async () => {
  if (!props.isEditing || !props.funnelId) return;

  try {
    loading.value = true;
    const response = await FunnelAPI.getById(props.funnelId);
    const funnelData = response.data;

    // Definir cada propriedade individualmente para garantir reatividade
    formData.value.name = funnelData.name || '';
    formData.value.description = funnelData.description || '';
    formData.value.active = funnelData.active !== undefined ? funnelData.active : true;
    formData.value.stages = funnelData.stages || {};
    formData.value.settings = funnelData.settings || {};
    formData.value.agents = (funnelData.settings?.agents || []).map(
      normalizeAgentForFunnel
    );
    formData.value.global_custom_attributes = Array.isArray(funnelData.global_custom_attributes)
      ? funnelData.global_custom_attributes.map(attr => ({
          name: attr.name || '',
          type: attr.type || '',
          field_type: attr.is_list ? 'list' : 'single',
          list_values: attr.list_values || [],
        }))
      : [];


    // Armazenar metadados para exibição
    funnelMetadata.value = {
      updated_at: funnelData.updated_at,
      optimization_history: funnelData.settings?.optimization_history || [],
    };

    // Emitir metadados para o header
    emit('metadata-updated', {
      updated_at: funnelData.updated_at,
      name: funnelData.name,
    });

    // Se tiver agentes pré-selecionados e eles não estiverem na lista, adiciona-os
    if (funnelData.settings?.agents?.length) {
      funnelData.settings.agents.forEach(agent => {
        if (!agentsList.value.find(a => a.id === agent.id)) {
          agentsList.value.push(agent);
        }
      });
    }

    // Inicializa as metas
    goals.value = funnelData.settings?.goals || [];

    // Inicializa os motivos de perda e ganho
    lossReasons.value = funnelData.settings?.loss_reasons || [];
    winReasons.value = funnelData.settings?.win_reasons || [];

    // Inicializa as etapas do template
    if (funnelData.stages) {
      stages.value = Object.entries(funnelData.stages)
        .map(([id, stage]) => ({
          id,
          name: stage.name,
          color: stage.color,
          description: stage.description,
          sla: stage.sla,
          capacity_limit: stage.capacity_limit,
          position: parseInt(stage.position, 10) || 0,
          auto_create_conditions: [...(stage.auto_create_conditions || [])],
          checklist_templates: [...(stage.checklist_templates || [])],
        }))
        .sort((a, b) => a.position - b.position);
    }
  } catch (error) {
    errorMessage.value = 'Erro ao carregar dados do funil';
  } finally {
    loading.value = false;
  }
};

// Compat HMR (template legado em memória): no-op — edição de condições foi removida do template.
const addCondition = () => {};

const removeCondition = () => {};

const getConditionTypeLabel = type => {
  const labels = {
    contact_has_tag: 'Contato tem tag',
    contact_has_custom_attribute: 'Atributo customizado',
    message_contains: 'Mensagem contém',
    conversation_has_priority: 'Prioridade da conversa',
    inbox_matches: 'Inbox específico',
    message_is_private: 'Mensagem privada contém',
    message_has_automation: 'Mensagem por automação contém',
    conversation_message_count: 'Quantidade de mensagens',
    last_message_age: 'Idade da última mensagem',
    message_not_read: 'Mensagem não lida',
    conversation_unassigned: 'Conversa sem agente',
    conversation_reopened: 'Conversa reaberta',
    conversation_snoozed: 'Conversa adiada',
  };
  return labels[type] || type;
};

const getOperatorLabel = operator => {
  const labels = {
    equal_to: 'Igual a',
    contains: 'Contém',
    not_equal_to: 'Diferente de',
    greater_than: 'Maior que',
    less_than: 'Menor que',
    greater_than_or_equal: 'Maior ou igual a',
    less_than_or_equal: 'Menor ou igual a',
  };
  return labels[operator] || operator;
};

const hasStageSla = stage =>
  stage.sla !== undefined && stage.sla !== null && stage.sla !== '';

const hasStageCapacityLimit = stage =>
  stage.capacity_limit !== undefined &&
  stage.capacity_limit !== null &&
  stage.capacity_limit !== '';

// Funções para manipular campos personalizados globais
const globalCustomAttributes = computed({
  get: () => formData.value.global_custom_attributes,
  set: val => {
    formData.value.global_custom_attributes = val;
  },
});

function addGlobalCustomAttribute() {
  globalCustomAttributes.value.push({ name: '', type: '', field_type: 'single', list_values: [] });
}

function removeGlobalCustomAttribute(index) {
  globalCustomAttributes.value.splice(index, 1);
}

function addListValue(attrIndex) {
  if (!globalCustomAttributes.value[attrIndex].list_values) {
    globalCustomAttributes.value[attrIndex].list_values = [];
  }
  globalCustomAttributes.value[attrIndex].list_values.push('');
}

function removeListValue(attrIndex, valueIndex) {
  globalCustomAttributes.value[attrIndex].list_values.splice(valueIndex, 1);
}

const handleSubmit = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';

    // Verifica o limite de funis para versão não PRO
    if (!isStacklab && !props.isEditing) {
      const { data: existingFunnels } = await FunnelAPI.get();
      if (existingFunnels.length >= 2) {
        errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_LIMIT_REACHED');
        return;
      }
    }

    // Preparar as etapas mantendo os templates existentes e atualizando posições
    const formattedStages = formatStagesForAPI();

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      active: formData.value.active,
      stages: formattedStages,
      settings: {
        agents: formData.value.agents.map(normalizeAgentForFunnel),
        goals: goals.value.filter(goal => goal.value && goal.value.toString().trim() !== ''),
        loss_reasons: lossReasons.value.filter(r => r.title.trim() !== ''),
        win_reasons: winReasons.value.filter(r => r.title.trim() !== ''),
        lead_forgotten_after_days: formData.value.settings?.lead_forgotten_after_days,
        round_robin_distribution: formData.value.settings?.round_robin_distribution,
        require_loss_reason: formData.value.settings?.require_loss_reason,
        default_currency: formData.value.settings?.default_currency,
      },
      global_custom_attributes: formData.value.global_custom_attributes
        .filter(attr => attr.name.trim() !== '' && attr.type.trim() !== '')
        .map(attr => {
          const base = {
            name: attr.name,
            type: attr.type,
            is_list: attr.field_type === 'list',
          };
          if (attr.field_type === 'list') {
            base.list_values = (attr.list_values || []).filter(v => v.trim() !== '');
          }
          return base;
        }),
    };

    let response;
    if (props.isEditing) {
      // Se estiver editando, faz update na API
      response = await FunnelAPI.update(props.funnelId, payload);
    } else {
      response = await FunnelAPI.create(payload);
    }

    if (response.data) {
      emit('saved', response.data);
    }
  } catch (error) {
    const responseErrors = error.response?.data?.errors;
    if (error.response?.data?.code === 'FUNNEL_LIMIT_REACHED') {
      errorMessage.value = error.response.data.error;
    } else if (responseErrors?.name?.length) {
      errorMessage.value = t('KANBAN.FUNNELS.FORM.ERRORS.NAME_TAKEN');
    } else if (responseErrors) {
      const formattedErrors = Object.values(responseErrors)
        .flat()
        .filter(Boolean)
        .join(', ');
      errorMessage.value = formattedErrors || t('KANBAN.FUNNELS.FORM.ERROR_GENERIC');
    } else {
      errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_GENERIC');
    }
  } finally {
    loading.value = false;
  }
};

const startEditing = stage => {
  newStage.value = {
    id: stage.id,
    name: stage.name,
    color: stage.color,
    description: stage.description,
    sla: stage.sla,
    capacity_limit: stage.capacity_limit,
    auto_create_conditions: [...(stage.auto_create_conditions || [])],
    checklist_templates: [...(stage.checklist_templates || [])],
  };
  editingStage.value = stage;
  nextTick(() => stageDialogRef.value?.open());
};

const saveStageEdit = index => {
  if (!editingStage.value) return;

  stages.value[index] = {
    ...stages.value[index],
    name: editingStage.value.name,
    description: editingStage.value.description,
    color: editingStage.value.color,
    auto_create_conditions: editingStage.value.auto_create_conditions,
    checklist_templates: stages.value[index].checklist_templates || [],
  };

  editingStage.value = null;

  // Se estiver editando, atualiza imediatamente
  if (props.isEditing) {
    handleOrderChange();
  }
};

const cancelEditingStage = () => {
  resetStageForm();
  stageDialogRef.value?.close();
};

// Watch para agentes
watch(
  () => formData.value.agents,
  newAgents => {
    // Handle agents update
  }
);

const toggleAgent = agent => {
  const index = formData.value.agents.findIndex(a => a.id === agent.id);
  if (index === -1) {
    formData.value.agents.push(agent);
  } else {
    formData.value.agents.splice(index, 1);
  }
};

// Compat HMR: computed usado apenas por template legado em cache.
const canAddCondition = computed(() => {
  const conditionlessTypes = [
    'message_not_read',
    'conversation_unassigned',
    'conversation_reopened',
    'conversation_snoozed',
  ];
  if (conditionlessTypes.includes(newCondition.value.type)) {
    return true;
  }
  return (
    newCondition.value.value &&
    newCondition.value.value.toString().trim() !== ''
  );
});

// Watch para atualizar unidade automaticamente baseada no tipo de meta
watch(
  () => newGoal.value.type,
  newType => {
    const unitMap = {
      conversion_rate: 'percentage',
      average_value: 'currency',
      average_time: 'days',
      total_conversions: 'count',
      total_revenue: 'currency',
    };
    newGoal.value.unit = unitMap[newType] || 'count';
  }
);

// Watch para monitorar mudanças nas etapas
watch(
  () => stages.value,
  newStages => {
    // Handle stages update
  },
  { deep: true }
);

watch(
  () => formData.value,
  newFormData => {
    // Handle formData update
  },
  { deep: true }
);

  watch(
    () => formData.value.global_custom_attributes,
    val => {
      if (!Array.isArray(val) || val.length === 0) {
        formData.value.global_custom_attributes = [{ name: '', type: '', field_type: 'single', list_values: [] }];
      }
    },
    { immediate: true }
  );

const agentSearch = ref('');
const agentDropdownOpen = ref(false);
const agentSelectorRef = ref(null);
const filteredAgents = computed(() => {
  if (!agentSearch.value)
    return agentsList.value.filter(
      a => !formData.value.agents.some(sel => sel.id === a.id)
    );
  return agentsList.value.filter(
    a =>
      !formData.value.agents.some(sel => sel.id === a.id) &&
      a.name.toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

const normalizeAgentForFunnel = agent => ({
  ...agent,
  is_supervisor: Boolean(agent.is_supervisor || agent.supervisor),
});

const inboxes = ref([]);
const loadingInboxes = ref(false);
function addAgent(agent) {
  if (!formData.value.agents.some(a => a.id === agent.id))
    formData.value.agents.push(normalizeAgentForFunnel(agent));
  agentDropdownOpen.value = false;
  agentSearch.value = '';
}
function removeAgent(agent) {
  formData.value.agents = formData.value.agents.filter(a => a.id !== agent.id);
}
function closeAgentDropdown(e) {
  if (!agentSelectorRef.value?.contains(e.target)) {
    agentDropdownOpen.value = false;
    window.removeEventListener('click', closeAgentDropdown);
  }
}
function openAgentDropdown() {
  agentDropdownOpen.value = true;
  setTimeout(() => {
    window.removeEventListener('click', closeAgentDropdown);
    window.addEventListener('click', closeAgentDropdown);
  }, 0);
}

function toggleSupervisor(agentId) {
  formData.value.agents = formData.value.agents.map(agent => ({
    ...agent,
    is_supervisor: agent.id === agentId ? !agent.is_supervisor : false,
  }));
}

// Controle de tooltip de cópia por etapa
const copiedStageId = ref({});

// Função para copiar o id da etapa
async function copyStageId(id) {
  navigator.clipboard.writeText(id);
  copiedStageId.value[id] = true;
  await nextTick();
  setTimeout(() => {
    copiedStageId.value[id] = false;
  }, 1500);
}

// Controle de expansão/colapso das etapas na pré-visualização

// Função para formatar valores monetários
const formatCurrency = value => {
  const currency = getCurrencyByCode(
    formData.value?.settings?.default_currency
  );
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
    }).format(value || 0);
  } catch {
    return `${currency.symbol} ${(value || 0).toFixed(2)}`;
  }
};






// Funções para gerenciar metas
const generateGoalId = () => {
  return `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const addGoal = () => {
  if (!newGoal.value.value) return;

  if (editingGoal.value) {
    const index = goals.value.findIndex(g => g.id === editingGoal.value.id);
    if (index !== -1) {
      goals.value[index] = {
        ...editingGoal.value,
        type: newGoal.value.type,
        value: newGoal.value.value,
        unit: newGoal.value.unit,
        description: newGoal.value.description,
        id: editingGoal.value.id,
      };
    }
    editingGoal.value = null;
  } else {
    goals.value.push({
      ...newGoal.value,
      id: generateGoalId(),
    });
  }

  newGoal.value = {
    id: '',
    type: 'conversion_rate',
    value: '',
    description: '',
    unit: 'percentage',
  };
  goalDialogRef.value?.close();
};

const removeGoal = (index) => {
  goals.value.splice(index, 1);
};

const startEditingGoal = (goal) => {
  newGoal.value = {
    id: goal.id,
    type: goal.type,
    value: goal.value,
    unit: goal.unit,
    description: goal.description,
  };
  editingGoal.value = goal;
  nextTick(() => goalDialogRef.value?.open());
};

const cancelEditingGoal = () => {
  resetGoalForm();
  goalDialogRef.value?.close();
};

const getGoalTypeLabel = (type) => {
  const labels = {
    conversion_rate: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.CONVERSION_RATE'),
    average_value: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.AVERAGE_VALUE'),
    average_time: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.AVERAGE_TIME'),
    total_conversions: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.TOTAL_CONVERSIONS'),
    total_revenue: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.TOTAL_REVENUE'),
  };
  return labels[type] || type;
};


const getUnitLabel = (unit) => {
  const labels = {
    percentage: '%',
    currency: 'R$',
    days: 'dias',
    hours: 'horas',
    count: '',
  };
  return labels[unit] || '';
};


// Funções para gerenciar motivos de perda e ganho
const addReason = () => {
  if (!newReason.value.title.trim()) return;

  const targetArray = newReason.value.type === 'loss' ? lossReasons : winReasons;

  if (editingReason.value) {
    const index = targetArray.value.findIndex(r => r.id === editingReason.value.id);
    if (index !== -1) {
      targetArray.value[index] = {
        ...editingReason.value,
        title: newReason.value.title,
        description: newReason.value.description,
      };
    }
    editingReason.value = null;
  } else {
    targetArray.value.push({
      id: `reason_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: newReason.value.title,
      description: newReason.value.description,
    });
  }

  newReason.value = { type: 'loss', title: '', description: '' };
  reasonDialogRef.value?.close();
};

const removeReason = (type, index) => {
  const targetArray = type === 'loss' ? lossReasons : winReasons;
  targetArray.value.splice(index, 1);
};

const startEditingReason = (type, reason) => {
  newReason.value = {
    type,
    title: reason.title,
    description: reason.description,
  };
  editingReason.value = reason;
  nextTick(() => reasonDialogRef.value?.open());
};

const cancelEditingReason = () => {
  resetReasonForm();
  reasonDialogRef.value?.close();
};

// Funções para gerenciar checklist presets
const addChecklistPreset = () => {
  if (!newChecklistItem.value.text.trim() || !selectedStageForChecklist.value) return;

  const stage = stages.value.find(s => s.id === selectedStageForChecklist.value);
  if (!stage) return;

  if (!stage.checklist_templates) {
    stage.checklist_templates = [];
  }

  if (editingChecklistItem.value) {
    const index = stage.checklist_templates.findIndex(c => c.id === editingChecklistItem.value.id);
    if (index !== -1) {
      stage.checklist_templates[index] = {
        ...editingChecklistItem.value,
        text: newChecklistItem.value.text,
        priority: newChecklistItem.value.priority,
        required: newChecklistItem.value.required,
      };
    }
  } else {
    stage.checklist_templates.push({
      id: `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: newChecklistItem.value.text,
      priority: newChecklistItem.value.priority,
      required: newChecklistItem.value.required,
    });
  }

  resetChecklistForm();
  checklistDialogRef.value?.close();
};

const removeChecklistPreset = (stageId, index) => {
  const stage = stages.value.find(s => s.id === stageId);
  if (!stage || !stage.checklist_templates) return;
  stage.checklist_templates.splice(index, 1);
};

const startEditingChecklistPreset = (stageId, item) => {
  selectedStageForChecklist.value = stageId;
  newChecklistItem.value = {
    text: item.text,
    priority: item.priority,
    required: item.required || false,
  };
  editingChecklistItem.value = item;
  nextTick(() => checklistDialogRef.value?.open());
};

const cancelEditingChecklistPreset = () => {
  resetChecklistForm();
  checklistDialogRef.value?.close();
};

const getChecklistPresetPriority = priority => {
  const priorityMap = {
    none: { label: 'Nenhuma', color: 'slate', iconSvg: null },
    low: {
      label: 'Baixa',
      color: 'green',
      iconSvg: '<path d="M7 13l3 3 3-3"/><path d="M7 6l3 3 3-3"/>',
    },
    medium: {
      label: 'Média',
      color: 'yellow',
      iconSvg: '<path d="M9 12l2 2 2-2"/><path d="M7 7l4 4 4-4"/>',
    },
    high: {
      label: 'Alta',
      color: 'orange',
      iconSvg: '<path d="M7 11l3-3 3 3"/><path d="M7 18l3-3 3 3"/>',
    },
    urgent: {
      label: 'Urgente',
      color: 'red',
      iconSvg:
        '<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>',
    },
  };
  return priorityMap[priority] || priorityMap.none;
};
</script>

<template>
  <div
    class="flex flex-1 min-h-0 flex-col h-full w-full bg-white dark:bg-slate-900"
  >
  <!-- Form Tabs -->
  <div class="tabs-container mb-6 shrink-0">
    <div class="tabs-scroll-wrapper">
      <button
        v-for="tab in formTabs"
        :key="tab.id"
        type="button"
        class="tab-button flex-shrink-0"
        :class="activeFormTab === tab.id ? 'tab-active' : 'tab-inactive'"
        @click.stop="activeFormTab = tab.id"
      >
        <div class="flex items-center gap-2">
          <span v-if="tab.customSvg" v-html="tab.customSvg" class="w-4 h-4 flex-shrink-0"></span>
          <fluent-icon v-else :icon="tab.icon" size="16" />
          {{ tab.label }}
        </div>
      </button>
    </div>
  </div>

  <form
    class="funnel-form scrollbar-hide flex-1 min-h-0 w-full overflow-y-auto"
    @submit.prevent="handleSubmit"
  >
    <!-- Mensagem de erro -->
    <div
      v-if="errorMessage"
      class="mb-4 p-4 bg-ruby-50 dark:bg-ruby-900/20 border border-ruby-200 dark:border-ruby-800 rounded-lg"
    >
      <div class="flex items-center gap-2">
        <fluent-icon
          icon="error-circle"
          size="20"
          class="text-ruby-500 dark:text-ruby-300"
        />
        <span class="text-ruby-700 dark:text-ruby-200">{{ errorMessage }}</span>
      </div>
    </div>

    <!-- Tab de Dados Básicos -->
    <div v-if="activeFormTab === 'basic-data'">
      <Dialog
        ref="stageDialogRef"
        width="3xl"
        type="edit"
        :show-cancel-button="false"
        :show-confirm-button="false"
        :overflow-y-auto="true"
        @close="handleStageDialogClose"
      >
        <div class="space-y-4">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-base font-medium leading-6 text-n-slate-12 min-w-0">
              {{ stageModalTitle }}
            </h3>
            <span
              v-if="editingStage"
              class="text-sm px-2 py-1 rounded shrink-0"
              :style="{
                backgroundColor: `${newStage.color}20`,
                color: newStage.color,
              }"
            >
              {{ t('KANBAN.FUNNELS.FORM.STAGES.EDITING_BADGE') }}
            </span>
          </div>
          <!-- Nome da Etapa -->
          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.STAGES.NAME_LABEL') }}
            </label>
            <input
              v-model="newStage.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.FUNNELS.FORM.STAGES.NAME_PLACEHOLDER')"
            />
          </div>
          <!-- Cor, Descrição, SLA e capacidade limite -->
          <div class="flex flex-col sm:flex-row items-start gap-2">
            <div class="w-full sm:w-[180px] shrink-0">
              <label class="block text-sm font-medium mb-2">
                {{ t('KANBAN.FUNNELS.FORM.STAGES.COLOR_LABEL') }}
              </label>
              <StageColorPicker v-model="newStage.color" />
            </div>
            <div class="flex-1 min-w-0 space-y-3 w-full">
              <div>
                <label class="block text-sm font-medium mb-2">
                  {{ t('KANBAN.FUNNELS.FORM.STAGES.DESCRIPTION_LABEL') }}
                </label>
                <input
                  v-model="newStage.description"
                  type="text"
                  class="w-full px-3 py-2 border rounded-lg"
                  :placeholder="t('KANBAN.FUNNELS.FORM.STAGES.STAGE_DESCRIPTION_PLACEHOLDER')"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ t('KANBAN.FUNNELS.FORM.STAGES.SLA_LABEL') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model.number="newStage.sla"
                    type="number"
                    min="0"
                    class="w-full px-3 py-2 border rounded-lg"
                    :placeholder="t('KANBAN.FUNNELS.FORM.STAGES.SLA_PLACEHOLDER')"
                  />
                  <span class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {{ t('KANBAN.FUNNELS.FORM.STAGES.SLA_UNIT') }}
                  </span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">
                  Capacidade limite da etapa
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model.number="newStage.capacity_limit"
                    type="number"
                    min="1"
                    class="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ex: 20"
                  />
                  <span class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    itens
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- Condições de Auto Criação -->
          <div class="mt-4">
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.STAGES.AUTO_CREATE_CONDITIONS') }}
            </label>

            <div
              class="mb-3 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2.5 text-xs leading-relaxed text-yellow-900 dark:border-yellow-800/80 dark:bg-yellow-900/20 dark:text-yellow-100"
              role="status"
            >
              {{ t('KANBAN.FUNNELS.FORM.AUTO_CREATION.READ_ONLY_HINT') }}
            </div>

            <!-- Lista de condições existentes (somente leitura) -->
            <div
              v-if="newStage.auto_create_conditions.length > 0"
              class="mb-3 space-y-2"
            >
              <div
                v-for="(condition, index) in newStage.auto_create_conditions"
                :key="index"
                class="flex flex-wrap items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded border"
              >
                <span
                  class="text-xs px-2 py-1 bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-300 rounded"
                >
                  {{ getConditionTypeLabel(condition.type) }}
                </span>
                <span
                  v-if="condition.attribute"
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ condition.attribute }}
                </span>
                <span
                  v-if="condition.operator"
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ getOperatorLabel(condition.operator) }}
                </span>
                <span
                  v-if="condition.value !== undefined && condition.value !== null && condition.value !== ''"
                  class="text-xs font-medium"
                >
                  {{ condition.value }}
                </span>
              </div>
            </div>
            <p
              v-else
              class="text-sm text-slate-500 dark:text-slate-400"
            >
              {{ t('KANBAN.FUNNELS.FORM.AUTO_CREATION.EMPTY_STATE') }}
            </p>
          </div>
        </div>
        <template #footer>
          <div class="flex w-full flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              color="slate"
              size="sm"
              :label="t('KANBAN.FUNNELS.FORM.ACTIONS.CANCEL')"
              @click="cancelEditingStage"
            />
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="sm"
              :disabled="!newStage.name"
              :label="editingStage ? t('KANBAN.FUNNELS.FORM.STAGES.SAVE_BUTTON') : t('KANBAN.FUNNELS.FORM.STAGES.ADD_BUTTON')"
              @click.stop="handleAddStage"
            />
          </div>
        </template>
      </Dialog>
      <div
        class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 gap-y-6 w-full min-w-0"
      >
        <!-- Coluna Esquerda - Formulário -->
        <div class="space-y-6 min-w-0">

          <!-- Status Ativo -->
          <div>
            <div class="flex items-center justify-between">
              <label class="block text-sm font-medium">
                {{ t('KANBAN.FUNNELS.FORM.ACTIVE.LABEL') }}
              </label>
              <Switch
                v-model="formData.active"
                size="small"
              />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-4">
              {{ t('KANBAN.FUNNELS.FORM.ACTIVE.HELP') }}
            </p>
          </div>

          <!-- Nome do Funil -->
          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.NAME.LABEL') }}
            </label>
            <input
              v-model="formData.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.FUNNELS.FORM.NAME.PLACEHOLDER')"
              required
            />
          </div>

          <!-- Descrição -->
          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.DESCRIPTION.LABEL') }}
            </label>
            <textarea
              v-model="formData.description"
              rows="3"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.FUNNELS.FORM.DESCRIPTION.PLACEHOLDER')"
            />
          </div>

          <!-- Agentes do Funil -->
          <div class="rounded-lg space-y-4">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2 text-sm font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users-icon lucide-users"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/></svg>
                <span>{{ t('KANBAN.FUNNELS.FORM.SECTIONS.FUNNEL_AGENTS') }}</span>
              </div>
            </div>
            <!-- Select com busca -->
            <div ref="agentSelectorRef" class="relative mb-2">
              <input
                v-model="agentSearch"
                type="text"
                class="w-full px-3 py-2 border rounded-lg"
                placeholder="Buscar agente..."
                @focus="openAgentDropdown"
                @input="openAgentDropdown"
              />
              <div
                v-if="agentDropdownOpen"
                class="absolute left-0 top-full w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto scrollbar-hide z-[99999]"
              >
                <div
                  v-for="agent in filteredAgents"
                  :key="agent.id"
                  class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2"
                  @click="addAgent(agent)"
                >
                  <Avatar
                    :name="agent.name"
                    :src="agent.avatar_url"
                    :size="24"
                    :status="agent.availability_status"
                  />
                  <div class="flex items-center gap-2 flex-1">
                    <span class="text-sm">{{ agent.name }}</span>
                    <span class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full capitalize">{{ agent.role }}</span>
                  </div>
                </div>
                <div
                  v-if="filteredAgents.length === 0"
                  class="px-3 py-2 text-slate-400 text-sm"
                >
                  {{ t('KANBAN.FUNNELS.FORM.AGENTS.NO_AGENT_FOUND') }}
                </div>
              </div>
            </div>
            <!-- Listagem compacta dos agentes selecionados -->
            <div v-if="formData.agents.length" class="space-y-1">
              <div
                class="flex items-center justify-between px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg mb-2"
              >
                <span class="text-sm text-slate-600 dark:text-slate-300">
                  {{ formData.agents.length }} agente{{
                    formData.agents.length !== 1 ? 's' : ''
                  }}
                  selecionado{{ formData.agents.length !== 1 ? 's' : '' }}
                </span>
                <button
                  type="button"
                  class="text-xs text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300"
                  @click="formData.agents = []"
                >
                  Limpar seleção
                </button>
              </div>
              <div
                v-for="agent in formData.agents"
                :key="agent.id"
                class="flex items-center gap-2 px-2 py-1 border border-slate-100 dark:border-slate-800 rounded mb-1"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url"
                  :size="24"
                  :status="agent.availability_status"
                />
                <div class="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                  <span class="text-sm">{{ agent.name }}</span>
                  <span class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full capitalize">{{ agent.role }}</span>
                  <span
                    v-if="agent.role === 'administrator'"
                    class="text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ t('KANBAN.FUNNELS.FORM.AGENTS.ADMIN_SEES_ALL_ITEMS') }}
                  </span>
                  <span
                    v-if="agent.role === 'agent'"
                    class="text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ t('KANBAN.FUNNELS.FORM.AGENTS.AGENT_SEES_ASSIGNED_ONLY') }}
                  </span>
                </div>
                <label
                  class="flex items-center gap-2 shrink-0 text-xs text-slate-600 dark:text-slate-300"
                >
                  <input
                    :checked="agent.is_supervisor"
                    type="checkbox"
                    class="h-4 w-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
                    @change="toggleSupervisor(agent.id)"
                  />
                  Supervisor
                </label>
                <button
                  type="button"
                  class="ml-auto shrink-0 text-xs text-ruby-500 hover:text-ruby-600"
                  @click="removeAgent(agent)"
                >
                  ✕
                </button>
              </div>
            </div>
            <!-- Estado vazio -->
            <div v-if="!formData.agents.length" class="text-center py-8 text-slate-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users-icon lucide-users mb-2 opacity-50 mx-auto"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/></svg>
              <p class="text-sm">{{ t('KANBAN.FUNNELS.FORM.AGENTS.NO_AGENT_SELECTED') }}</p>
              <p class="text-xs">{{ t('KANBAN.FUNNELS.FORM.AGENTS.SEARCH_HINT') }}</p>
            </div>
          </div>

        </div>

        <!-- Coluna Direita - Lista de Etapas -->
        <div class="space-y-6 min-w-0 w-full">
          <div>
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2 text-sm font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-columns3-icon lucide-columns-3">
                  <rect width="18" height="18" x="3" y="3" rx="2"/>
                  <path d="M9 3v18"/>
                  <path d="M15 3v18"/>
                </svg>
                <span>{{ t('KANBAN.FUNNELS.FORM.SECTIONS.FUNNEL_STAGES') }}</span>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  type="button"
                  variant="solid"
                  color="blue"
                  size="sm"
                  :label="t('KANBAN.FUNNELS.FORM.STAGES.ADD_NEW_BUTTON')"
                  @click="openNewStageModal"
                >
                  <template #icon>
                    <fluent-icon icon="add" size="16" />
                  </template>
                </Button>
                <span class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full">
                  {{ stages.length }}
                </span>
              </div>
            </div>

            <!-- Lista de Etapas -->
            <div class="space-y-2">
              <draggable
                v-model="stages"
                :options="dragOptions"
                item-key="id"
                class="space-y-2"
                @change="handleOrderChange"
              >
                <template #item="{ element: stage, index }">
                  <div
                    class="stage-preview-card flex flex-col border border-slate-100 dark:border-slate-800 rounded-lg cursor-move group transition-all duration-200 hover:shadow-md bg-white dark:bg-slate-800"
                  >
                    <!-- Header do Card -->
                    <div
                      class="flex items-center justify-between p-3 border-b border-slate-100 dark:border-slate-700 rounded-t-lg"
                    >
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          class="w-3 h-3 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: stage.color }"
                        />
                        <div class="flex-1 min-w-0">
                          <div
                            class="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate"
                            :title="stage.name"
                          >
                            {{ stage.name }}
                          </div>
                          <div v-if="stage.description" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                            {{ stage.description }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center gap-1 ml-2">
                        <div class="relative">
                          <button
                            type="button"
                            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            @click.stop="copyStageId(stage.id)"
                            :title="'Copiar ID: ' + stage.id"
                          >
                            <fluent-icon icon="copy" size="12" />
                          </button>
                          <span
                            v-if="copiedStageId[stage.id]"
                            class="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded bg-slate-700 text-white whitespace-nowrap z-10"
                            >Copiado!</span
                          >
                        </div>
                        <button
                          type="button"
                          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          @click.stop="startEditing(stage)"
                          title="Editar etapa"
                        >
                          <fluent-icon icon="edit" size="12" />
                        </button>
                        <button
                          type="button"
                          class="text-slate-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          @click.stop="removeStage(index)"
                          title="Remover etapa"
                        >
                          <fluent-icon icon="delete" size="12" />
                        </button>
                      </div>
                    </div>

                    <!-- Conteúdo do Card -->
                    <div class="p-3 space-y-3">
                      <!-- SLA da etapa -->
                      <div
                        v-if="hasStageSla(stage)"
                        class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2"
                      >
                        <div class="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-amber-600 dark:text-amber-400 shrink-0">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {{ t('KANBAN.FUNNELS.FORM.STAGES.SLA_LABEL') }}
                        </div>
                        <div class="text-xs text-amber-700 dark:text-amber-300">
                          {{ stage.sla }}
                          {{ t('KANBAN.FUNNELS.FORM.STAGES.SLA_UNIT') }}
                        </div>
                      </div>

                      <div
                        v-if="hasStageCapacityLimit(stage)"
                        class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2"
                      >
                        <div class="text-xs font-medium text-purple-800 dark:text-purple-200 mb-1 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-purple-600 dark:text-purple-400 shrink-0"
                          >
                            <rect x="9" y="2" width="6" height="4" rx="1" />
                            <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
                            <path d="M9 12h6" />
                            <path d="M9 16h4" />
                          </svg>
                          Capacidade limite
                        </div>
                        <div class="text-xs text-purple-700 dark:text-purple-300">
                          {{ stage.capacity_limit }} itens
                        </div>
                      </div>

                      <!-- Condições de Auto Criação -->
                      <div
                        v-if="stage.auto_create_conditions && stage.auto_create_conditions.length > 0"
                        class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2"
                      >
                        <div class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-1">
                          <fluent-icon icon="settings" size="10" />
                          Auto criação
                        </div>
                        <div class="flex flex-wrap gap-1">
                          <span
                            v-for="(condition, index) in stage.auto_create_conditions"
                            :key="index"
                            class="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full"
                          >
                            <span class="truncate">{{ getConditionTypeLabel(condition.type) }}</span>
                            <span class="font-medium">{{ condition.value }}</span>
                          </span>
                        </div>
                      </div>

                      <!-- Checklist Templates -->
                      <div
                        v-if="stage.checklist_templates && stage.checklist_templates.length > 0"
                        class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2"
                      >
                        <div class="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-600">
                            <path d="M16 5H3"/>
                            <path d="M16 12H3"/>
                            <path d="M11 19H3"/>
                            <path d="m15 18 2 2 4-4"/>
                          </svg>
                          Checklist
                        </div>
                        <div class="text-xs text-slate-600 dark:text-slate-400">
                          {{ stage.checklist_templates.length }} item{{ stage.checklist_templates.length !== 1 ? 's' : '' }} pré-configurado{{ stage.checklist_templates.length !== 1 ? 's' : '' }}
                        </div>
                      </div>

                      <!-- Estado vazio -->
                      <div
                        v-if="(!stage.auto_create_conditions || stage.auto_create_conditions.length === 0) && (!stage.checklist_templates || stage.checklist_templates.length === 0) && !hasStageSla(stage) && !hasStageCapacityLimit(stage)"
                        class="text-center py-4 text-slate-400 dark:text-slate-500"
                      >
                        <fluent-icon icon="info" size="16" class="mx-auto mb-1 opacity-50" />
                        <p class="text-xs">{{ t('KANBAN.FUNNELS.FORM.STAGES.PREVIEW_NO_EXTRA_INFO') }}</p>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>

              <!-- Empty State -->
              <div
                v-if="!stages.length"
                class="flex flex-col items-center justify-center py-8 text-slate-400"
              >
                <fluent-icon icon="list" size="32" class="mb-2" />
                <p class="text-sm">
                  {{ t('KANBAN.FUNNELS.FORM.STAGES.EMPTY') }}
                </p>
              </div>
            </div>
          </div>



          <!-- Status e Configurações -->
          <div class="mt-6 space-y-4">
            <!-- Histórico de Otimizações -->
            <div v-if="funnelMetadata.optimization_history?.length > 0" class="space-y-2">
              <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
                Histórico de Otimizações
              </label>
              <div class="space-y-1">
                <div
                  v-for="(optimization, index) in funnelMetadata.optimization_history.slice(-3)"
                  :key="index"
                  class="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded"
                >
                  {{ new Date(optimization.date).toLocaleDateString() }} - {{ optimization.type }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab de Campos Personalizados Globais -->
    <div v-if="activeFormTab === 'global-fields'">
          <div
            class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
          >
            <div class="space-y-4">
              <div
                v-for="(attr, idx) in globalCustomAttributes || []"
                :key="idx"
                class="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2"
              >
                <div class="flex gap-2 items-center">
                  <input
                    v-model="attr.name"
                    type="text"
                    class="w-1/2 rounded-lg"
                    placeholder="Chave (name)"
                  />
                  <select v-model="attr.type" class="w-1/2 rounded-lg">
                    <option value="" disabled selected>Tipo</option>
                    <option value="string">Texto</option>
                    <option value="number">Número</option>
                    <option value="date">Data</option>
                    <option value="boolean">Verdadeiro/Falso</option>
                  </select>
                  <select
                    v-model="attr.field_type"
                    class="px-2 py-1 border rounded text-sm w-36 h-10 align-middle"
                  >
                    <option value="single">Único</option>
                    <option value="list">Lista</option>
                  </select>
                  <button
                    type="button"
                    @click="removeGlobalCustomAttribute(idx)"
                    class="p-1 text-ruby-500"
                  >
                    ✕
                  </button>
                </div>
                
                <!-- Input para valores da lista -->
                <div v-if="attr.field_type === 'list'" class="pl-2 space-y-2">
                  <label class="text-xs font-medium text-slate-600 dark:text-slate-400">{{ t('KANBAN.FUNNELS.FORM.GLOBAL_CUSTOM_FIELDS.LIST_VALUES_LABEL') }}</label>
                  <div
                    v-for="(value, valueIdx) in attr.list_values || []"
                    :key="valueIdx"
                    class="flex gap-2 items-center"
                  >
                    <input
                      v-model="attr.list_values[valueIdx]"
                      type="text"
                      class="flex-1 px-2 py-1 border rounded text-sm"
                      :placeholder="t('KANBAN.FUNNELS.FORM.GLOBAL_CUSTOM_FIELDS.VALUE_PLACEHOLDER')"
                    />
                    <button
                      type="button"
                      @click="removeListValue(idx, valueIdx)"
                      class="text-ruby-500 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="addListValue(idx)"
                    class="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-600"
                  >
                    {{ t('KANBAN.FUNNELS.FORM.GLOBAL_CUSTOM_FIELDS.ADD_VALUE') }}
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="addGlobalCustomAttribute"
                class="mt-2 px-3 py-1 bg-woot-500 text-white rounded"
              >
                {{ t('KANBAN.ITEM.CUSTOM_FIELDS.ADD_FIELD') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Tab de Configurações -->
        <div v-if="activeFormTab === 'settings'">
          <div class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-6">
            <!-- Round Robin -->
            <div>
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium">
                  {{ t('KANBAN.FUNNELS.FORM.SETTINGS.ROUND_ROBIN_LABEL') }}
                </label>
                <Switch
                  v-model="formData.settings.round_robin_distribution"
                  size="small"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ t('KANBAN.FUNNELS.FORM.SETTINGS.ROUND_ROBIN_HELP') }}
              </p>
            </div>

            <!-- Require Loss Reason -->
            <div>
              <div class="flex items-center justify-between">
                <label class="block text-sm font-medium">
                  {{ t('KANBAN.FUNNELS.FORM.SETTINGS.REQUIRE_LOSS_REASON_LABEL') }}
                </label>
                <Switch
                  v-model="formData.settings.require_loss_reason"
                  size="small"
                />
              </div>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ t('KANBAN.FUNNELS.FORM.SETTINGS.REQUIRE_LOSS_REASON_HELP') }}
              </p>
            </div>

            <!-- Default Currency -->
            <div>
              <label class="block text-sm font-medium mb-2">
                {{ t('KANBAN.FUNNELS.FORM.SETTINGS.DEFAULT_CURRENCY_LABEL') }}
              </label>
              <select
                v-model="formData.settings.default_currency"
                class="w-full px-3 py-2 border rounded-lg"
              >
                <option value="COP">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_COP') }}</option>
                <option value="USD">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_USD') }}</option>
                <option value="EUR">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_EUR') }}</option>
                <option value="BRL">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_BRL') }}</option>
                <option value="GBP">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_GBP') }}</option>
              </select>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ t('KANBAN.FUNNELS.FORM.SETTINGS.DEFAULT_CURRENCY_HELP') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Tab de Metas -->
        <div v-if="activeFormTab === 'goals'">
          <div class="flex justify-end mb-4">
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="sm"
              :label="t('KANBAN.FUNNELS.FORM.GOALS.ADD_NEW_BUTTON')"
              @click="openNewGoalModal"
            >
              <template #icon>
                <fluent-icon icon="add" size="16" />
              </template>
            </Button>
          </div>

          <Dialog
            ref="goalDialogRef"
            width="lg"
            type="edit"
            :title="goalModalTitle"
            :show-cancel-button="false"
            :show-confirm-button="false"
            @close="handleGoalDialogClose"
          >
            <div class="space-y-4">
              <div
                v-if="editingGoal"
                class="flex justify-end"
              >
                <span
                  class="text-sm px-2 py-1 rounded"
                  style="background-color: #3B82F620; color: #3B82F6;"
                >
                  {{ t('KANBAN.FUNNELS.FORM.GOALS.EDITING_BADGE') }}
                </span>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.GOALS.TYPE_LABEL') }}</label>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="(type, key) in {
                      conversion_rate: { label: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.CONVERSION_RATE'), color: '#3B82F6' },
                      average_value: { label: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.AVERAGE_VALUE'), color: '#10B981' },
                      average_time: { label: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.AVERAGE_TIME'), color: '#F59E0B' },
                      total_conversions: { label: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.TOTAL_CONVERSIONS'), color: '#8B5CF6' },
                      total_revenue: { label: t('KANBAN.FUNNELS.FORM.GOALS.TYPES.TOTAL_REVENUE'), color: '#EF4444' }
                    }"
                    :key="key"
                    type="button"
                    class="flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors"
                    :style="newGoal.type === key
                      ? {
                          borderColor: type.color,
                          backgroundColor: type.color + '80',
                          color: type.color
                        }
                      : {
                          borderColor: type.color,
                          backgroundColor: 'transparent',
                          color: type.color
                        }"
                    @click="newGoal.type = key"
                  >
                    <span class="text-sm">{{ type.label }}</span>
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.GOALS.VALUE_LABEL') }}</label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="newGoal.value"
                    :type="['percentage', 'currency', 'count'].includes(newGoal.unit) ? 'number' : 'text'"
                    class="w-full px-3 py-2 border rounded-lg"
                    :placeholder="newGoal.type === 'conversion_rate' ? t('KANBAN.FUNNELS.FORM.GOALS.VALUE_PLACEHOLDER.CONVERSION_RATE') :
                                newGoal.type === 'average_value' ? t('KANBAN.FUNNELS.FORM.GOALS.VALUE_PLACEHOLDER.AVERAGE_VALUE') :
                                newGoal.type === 'average_time' ? t('KANBAN.FUNNELS.FORM.GOALS.VALUE_PLACEHOLDER.AVERAGE_TIME') : t('KANBAN.FUNNELS.FORM.GOALS.VALUE_PLACEHOLDER.DEFAULT')"
                  />
                  <span class="text-sm text-slate-500 px-2">{{ getUnitLabel(newGoal.unit) }}</span>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.GOALS.DESCRIPTION_LABEL') }}</label>
                <input
                  v-model="newGoal.description"
                  type="text"
                  class="w-full px-3 py-2 border rounded-lg"
                  :placeholder="t('KANBAN.FUNNELS.FORM.GOALS.DESCRIPTION_PLACEHOLDER')"
                />
              </div>
            </div>
            <template #footer>
              <div class="flex w-full flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  color="slate"
                  size="sm"
                  :label="t('KANBAN.FUNNELS.FORM.ACTIONS.CANCEL')"
                  @click="cancelEditingGoal"
                />
                <Button
                  type="button"
                  variant="solid"
                  color="blue"
                  size="sm"
                  :disabled="!newGoal.value"
                  :label="editingGoal ? t('KANBAN.FUNNELS.FORM.ACTIONS.SAVE') : t('KANBAN.FUNNELS.FORM.GOALS.ADD_BUTTON')"
                  @click="addGoal"
                />
              </div>
            </template>
          </Dialog>

          <!-- Lista de Metas Configuradas -->
          <div v-if="goals.length > 0" class="mt-4 space-y-2">
            <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">{{ t('KANBAN.FUNNELS.FORM.GOALS.CONFIGURED_TITLE') }}</h4>
            <div
              v-for="(goal, index) in goals"
              :key="goal.id"
              class="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium">{{ getGoalTypeLabel(goal.type) }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <span class="text-sm font-medium text-blue-600 dark:text-blue-400">{{ goal.value }}</span>
                  <span class="text-xs text-slate-500">{{ getUnitLabel(goal.unit) }}</span>
                </div>
                <div v-if="goal.description" class="text-xs text-slate-500 italic">
                  {{ goal.description }}
                </div>
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="text-slate-400 hover:text-slate-500 p-1"
                  @click="startEditingGoal(goal)"
                >
                  <fluent-icon icon="edit" size="14" />
                </button>
                <button
                  type="button"
                  class="text-slate-400 hover:text-slate-500 p-1"
                  @click="removeGoal(index)"
                >
                  <fluent-icon icon="delete" size="14" />
                </button>
              </div>
            </div>
          </div>

          <!-- Estado vazio -->
          <div v-else class="mt-4 text-center py-8 text-slate-400">
            <p class="text-sm">{{ t('KANBAN.FUNNELS.FORM.GOALS.EMPTY_STATE') }}</p>
            <p class="text-xs">{{ t('KANBAN.FUNNELS.FORM.GOALS.EMPTY_DESCRIPTION') }}</p>
          </div>
        </div>

        <!-- Tab de Motivos de Perda e Ganho -->
        <div v-if="activeFormTab === 'loss-win-reasons'">
          <div class="flex justify-end mb-4">
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="sm"
              :label="t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.ADD_NEW_BUTTON')"
              @click="openNewReasonModal"
            >
              <template #icon>
                <fluent-icon icon="add" size="16" />
              </template>
            </Button>
          </div>

          <Dialog
            ref="reasonDialogRef"
            width="lg"
            type="edit"
            :title="reasonModalTitle"
            :show-cancel-button="false"
            :show-confirm-button="false"
            @close="handleReasonDialogClose"
          >
            <div class="space-y-4">
              <div
                v-if="editingReason"
                class="flex justify-end"
              >
                <span
                  class="text-sm px-2 py-1 rounded"
                  :style="{
                    backgroundColor: newReason.type === 'loss' ? '#E11D4820' : '#14B8A620',
                    color: newReason.type === 'loss' ? '#E11D48' : '#14B8A6'
                  }"
                >
                  {{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.EDITING_BADGE') }}
                </span>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.TYPE_LABEL') }}</label>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors"
                    :style="newReason.type === 'loss'
                      ? {
                          borderColor: '#E11D48',
                          backgroundColor: '#FFF1F2',
                          color: '#E11D48'
                        }
                      : {
                          borderColor: '#E2E8F0',
                          backgroundColor: 'transparent',
                          color: '#64748B'
                        }"
                    @click="newReason.type = 'loss'"
                  >
                    <span class="text-sm font-medium">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.TYPE_LOSS') }}</span>
                  </button>
                  <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors"
                    :style="newReason.type === 'win'
                      ? {
                          borderColor: '#14B8A6',
                          backgroundColor: '#F0FDFA',
                          color: '#14B8A6'
                        }
                      : {
                          borderColor: '#E2E8F0',
                          backgroundColor: 'transparent',
                          color: '#64748B'
                        }"
                    @click="newReason.type = 'win'"
                  >
                    <span class="text-sm font-medium">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.TYPE_WIN') }}</span>
                  </button>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.TITLE_LABEL') }}</label>
                <input
                  v-model="newReason.title"
                  type="text"
                  class="w-full px-3 py-2 border rounded-lg"
                  :placeholder="t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.TITLE_PLACEHOLDER')"
                />
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.DESCRIPTION_LABEL') }}</label>
                <textarea
                  v-model="newReason.description"
                  rows="2"
                  class="w-full px-3 py-2 border rounded-lg"
                  :placeholder="t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.DESCRIPTION_PLACEHOLDER')"
                />
              </div>
            </div>
            <template #footer>
              <div class="flex w-full flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  color="slate"
                  size="sm"
                  :label="t('KANBAN.FUNNELS.FORM.ACTIONS.CANCEL')"
                  @click="cancelEditingReason"
                />
                <Button
                  type="button"
                  variant="solid"
                  :color="newReason.type === 'loss' ? 'ruby' : 'teal'"
                  size="sm"
                  :disabled="!newReason.title.trim()"
                  :label="editingReason ? t('KANBAN.FUNNELS.FORM.ACTIONS.SAVE') : t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.ADD_BUTTON')"
                  @click="addReason"
                />
              </div>
            </template>
          </Dialog>

          <!-- Listas de Motivos -->
          <div class="mt-4 grid grid-cols-2 gap-4">
            <!-- Motivos de Perda -->
            <div>
              <h4 class="text-sm font-medium text-ruby-700 dark:text-ruby-300 mb-3 flex items-center gap-2">
                <span>{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.LOSS_REASONS_TITLE') }}</span>
                <span class="px-2 py-0.5 text-xs bg-ruby-100 dark:bg-ruby-900/20 rounded-full">{{ lossReasons.length }}</span>
              </h4>
              <div v-if="lossReasons.length > 0" class="space-y-2">
                <div
                  v-for="(reason, index) in lossReasons"
                  :key="reason.id"
                  class="p-3 rounded-lg border"
                  style="background-color: #FFF1F2; border-color: #FFE4E6;"
                >
                  <div class="flex items-start justify-between mb-1">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ reason.title }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-1"
                        @click="startEditingReason('loss', reason)"
                      >
                        <fluent-icon icon="edit" size="14" />
                      </button>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-1"
                        @click="removeReason('loss', index)"
                      >
                        <fluent-icon icon="delete" size="14" />
                      </button>
                    </div>
                  </div>
                  <p v-if="reason.description" class="text-xs text-slate-500 dark:text-slate-400">
                    {{ reason.description }}
                  </p>
                </div>
              </div>
              <div v-else class="text-center py-4 text-slate-400">
                <p class="text-xs">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.EMPTY_LOSS') }}</p>
              </div>
            </div>

            <!-- Motivos de Ganho -->
            <div>
              <h4 class="text-sm font-medium text-teal-700 dark:text-teal-300 mb-3 flex items-center gap-2">
                <span>{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.WIN_REASONS_TITLE') }}</span>
                <span class="px-2 py-0.5 text-xs bg-teal-100 dark:bg-teal-900/20 rounded-full">{{ winReasons.length }}</span>
              </h4>
              <div v-if="winReasons.length > 0" class="space-y-2">
                <div
                  v-for="(reason, index) in winReasons"
                  :key="reason.id"
                  class="p-3 rounded-lg border"
                  style="background-color: #F0FDFA; border-color: #CCFBF1;"
                >
                  <div class="flex items-start justify-between mb-1">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ reason.title }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-1"
                        @click="startEditingReason('win', reason)"
                      >
                        <fluent-icon icon="edit" size="14" />
                      </button>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-1"
                        @click="removeReason('win', index)"
                      >
                        <fluent-icon icon="delete" size="14" />
                      </button>
                    </div>
                  </div>
                  <p v-if="reason.description" class="text-xs text-slate-500 dark:text-slate-400">
                    {{ reason.description }}
                  </p>
                </div>
              </div>
              <div v-else class="text-center py-4 text-slate-400">
                <p class="text-xs">{{ t('KANBAN.FUNNELS.FORM.LOSS_WIN_REASONS.EMPTY_WIN') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab de Checklist Presets -->
        <div v-if="activeFormTab === 'checklist-presets'">
          <Dialog
            ref="checklistDialogRef"
            width="lg"
            type="edit"
            :title="checklistModalTitle"
            :show-cancel-button="false"
            :show-confirm-button="false"
            @close="handleChecklistDialogClose"
          >
            <div class="space-y-4">
              <div
                v-if="selectedStageNameForChecklistModal"
                class="text-sm text-slate-600 dark:text-slate-400"
              >
                <span class="font-medium text-slate-700 dark:text-slate-300">{{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.STAGE_LABEL') }}:</span>
                {{ selectedStageNameForChecklistModal }}
              </div>
              <div
                v-if="editingChecklistItem"
                class="flex justify-end"
              >
                <span
                  class="text-sm px-2 py-1 rounded"
                  style="background-color: #3B82F620; color: #3B82F6;"
                >
                  {{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.EDITING_BADGE') }}
                </span>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.TEXT_LABEL') }}</label>
                <textarea
                  v-model="newChecklistItem.text"
                  rows="3"
                  class="w-full px-3 py-2 border rounded-lg"
                  :placeholder="t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.TEXT_PLACEHOLDER')"
                />
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">{{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.PRIORITY_LABEL') }}</label>
                <select
                  v-model="newChecklistItem.priority"
                  class="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="none">Nenhuma</option>
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>

              <div>
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium">
                    Obrigatório
                  </label>
                  <Switch
                    v-model="newChecklistItem.required"
                    size="small"
                  />
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Marque se este item deve ser obrigatoriamente concluído
                </p>
              </div>
            </div>
            <template #footer>
              <div class="flex w-full flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  color="slate"
                  size="sm"
                  :label="t('KANBAN.FUNNELS.FORM.ACTIONS.CANCEL')"
                  @click="cancelEditingChecklistPreset"
                />
                <Button
                  type="button"
                  variant="solid"
                  color="blue"
                  size="sm"
                  :disabled="!newChecklistItem.text.trim()"
                  :label="editingChecklistItem ? t('KANBAN.FUNNELS.FORM.ACTIONS.SAVE') : t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.ADD_BUTTON')"
                  @click="addChecklistPreset"
                />
              </div>
            </template>
          </Dialog>

          <!-- Checklist presets agrupados por etapa -->
          <div
            v-if="!stages.length"
            class="text-center py-10 text-slate-500 dark:text-slate-400"
          >
            <fluent-icon icon="list" size="48" class="mx-auto mb-4 opacity-50" />
            <p class="text-sm">{{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.NO_STAGES_HINT') }}</p>
          </div>

          <div
            v-else
            class="space-y-10 mt-2"
          >
            <div
              v-for="stage in stages"
              :key="stage.id"
              class="space-y-3"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div class="flex min-w-0 flex-1 items-center gap-2">
                  <div class="h-2.5 w-2.5 shrink-0 rounded-full" :style="{ backgroundColor: stage.color }" />
                  <h4 class="flex min-w-0 items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-slate-600 dark:text-slate-400">
                      <path d="M16 5H3" />
                      <path d="M16 12H3" />
                      <path d="M11 19H3" />
                      <path d="m15 18 2 2 4-4" />
                    </svg>
                    <span class="truncate">{{ stage.name }}</span>
                    <span class="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      {{ (stage.checklist_templates || []).length }}
                    </span>
                    <span class="shrink-0 text-xs font-normal text-slate-500 dark:text-slate-400">
                      {{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.AUTO_CREATE_HINT') }}
                    </span>
                  </h4>
                </div>
                <Button
                  type="button"
                  variant="solid"
                  color="blue"
                  size="sm"
                  :label="t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.ADD_NEW_BUTTON')"
                  @click="openNewChecklistModal(stage.id)"
                >
                  <template #icon>
                    <fluent-icon icon="add" size="16" />
                  </template>
                </Button>
              </div>

              <div
                v-if="(stage.checklist_templates || []).length > 0"
                class="preset-checklist-section space-y-2"
              >
                <div
                  v-for="(item, index) in stage.checklist_templates"
                  :key="item.id"
                  class="preset-checklist-item group"
                >
                  <div class="flex items-start gap-3">
                    <div
                      class="mt-0.5 flex-shrink-0 text-slate-500 dark:text-slate-400"
                      aria-hidden="true"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-list-todo"
                      >
                        <path d="M13 5h8" />
                        <path d="M13 12h8" />
                        <path d="M13 19h8" />
                        <path d="m3 17 2 2 4-4" />
                        <rect
                          x="3"
                          y="4"
                          width="6"
                          height="6"
                          rx="1"
                        />
                      </svg>
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-2">
                        <div class="preset-checklist-text whitespace-pre-wrap">
                          {{ item.text }}
                        </div>
                        <div class="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            @click="startEditingChecklistPreset(stage.id, item)"
                          >
                            <fluent-icon icon="edit" size="14" />
                          </button>
                          <button
                            type="button"
                            class="p-1 text-slate-400 hover:text-n-ruby-9 dark:hover:text-n-ruby-4"
                            @click="removeChecklistPreset(stage.id, index)"
                          >
                            <fluent-icon icon="delete" size="14" />
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="getChecklistPresetPriority(item.priority).label !== 'Nenhuma' || item.required"
                        class="preset-checklist-metadata mt-2 flex flex-wrap items-center gap-2"
                      >
                        <div
                          v-if="getChecklistPresetPriority(item.priority).label !== 'Nenhuma'"
                          class="preset-checklist-priority"
                        >
                          <span
                            class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                            :class="{
                              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300': getChecklistPresetPriority(item.priority).color === 'red',
                              'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300': getChecklistPresetPriority(item.priority).color === 'orange',
                              'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300': getChecklistPresetPriority(item.priority).color === 'yellow',
                              'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300': getChecklistPresetPriority(item.priority).color === 'green',
                            }"
                          >
                            <svg
                              v-if="getChecklistPresetPriority(item.priority).iconSvg"
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="flex-shrink-0"
                              v-html="getChecklistPresetPriority(item.priority).iconSvg"
                            />
                            {{ getChecklistPresetPriority(item.priority).label }}
                          </span>
                        </div>
                        <span
                          v-if="item.required"
                          class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        >
                          {{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.REQUIRED_BADGE') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="rounded-lg border border-dashed border-slate-200 py-8 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400"
              >
                <fluent-icon icon="list" size="40" class="mx-auto mb-3 opacity-50" />
                <p class="text-sm">{{ t('KANBAN.FUNNELS.FORM.CHECKLIST_PRESETS.EMPTY_STATE') }}</p>
              </div>
            </div>
          </div>
        </div>
  </form>
  </div>
</template>

<style lang="scss" scoped>
.border-woot {
  border-color: var(--color-woot);
  border-width: 1px;
}

.ghost-card {
  opacity: 0.5;
  background: #c8ebfb;
}

.cursor-move {
  cursor: move;
}

.loading-spinner {
  @apply w-6 h-6 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}

.agent-card {
  @apply transition-all duration-200;

  &:hover {
    @apply transform scale-[1.02];
  }
}

// Oculta a scrollbar mas permite scroll
.scrollbar-hide {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE 10+ */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none !important; /* Chrome/Safari/Webkit */
}

/* Força ocultar scrollbar em todos os elementos */
.funnel-form {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
.funnel-form::-webkit-scrollbar {
  display: none !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 500px;
}
.stage-preview-card {
  position: relative;
}
.stage-preview-card {
  background: var(--tw-bg-opacity, 1) var(--tw-gradient, none);
}
.stage-preview-card:hover {
  background: var(--tw-gradient);
}
.stage-preview-details {
  /* Remove bg e gradiente daqui, fica só no card externo */
}

// Estilos para as abas
.tabs-container {
  margin-bottom: 1rem;
}

.tab-button.tab-active {
  @apply text-woot-500;
}

.tab-button.tab-active::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500;
}

.tab-button.tab-inactive {
  @apply text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100;
}

.tab-inactive:hover {
  color: #64748b;
}

.dark .tab-inactive {
  color: #94a3b8;
}

.dark .tab-inactive:hover {
  color: #64748b;
}

/* Estilos para as abas com scroll lateral */
.tabs-container {
  overflow: hidden;
}

.tabs-scroll-wrapper {
  display: flex;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-behavior: smooth;
}

.tabs-scroll-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.tab-button {
  @apply px-4 py-2 text-sm font-medium relative transition-colors whitespace-nowrap;
}

/* Mobile: ajustes específicos para abas */
@media (max-width: 768px) {
  .tab-button {
    @apply px-3 py-2 text-xs;
  }

  .tabs-scroll-wrapper {
    padding-bottom: 2px; /* Espaço para o scroll */
  }
}

// Estilos para o colapso vertical do grupo Dados Básicos
.collapsible-content {
  transition: all 0.3s ease-in-out;
}

// Classe para limitar linhas de texto
.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

/* Preset checklist list — aligned with KanbanChecklistTab.vue */
.preset-checklist-section {
  @apply space-y-2;
}

.preset-checklist-item {
  @apply bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700
    hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200;
}

.preset-checklist-text {
  @apply flex-1 text-slate-700 dark:text-slate-300 text-sm leading-relaxed break-words overflow-hidden;
}

.preset-checklist-metadata {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.preset-checklist-priority {
  @apply flex-shrink-0;
}

@media (max-width: 640px) {
  .preset-checklist-item {
    @apply p-3;
  }
}
</style>
